/**
 * Phase 22 Plan 22-03 — Shared form fields for Client create/edit forms.
 *
 * D-225 validation rules:
 *   Name: 3-200 chars
 *   Code: 2-30 chars, regex ^[A-Za-z0-9_-]+$
 *   ContactEmail: RFC-ish pattern (type="text" not "email" per Phase 20 ME-02 lesson)
 *   ContactPhone: digits only after strip
 *   GstNumber: ≤30 chars
 *   PanNumber: ≤20 chars
 */

import type { JSX } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import type { ClientFormValues } from './clientFormSchema';

interface ClientFormFieldsProps {
  readonly form: UseFormReturn<ClientFormValues>;
}

export function ClientFormFields({ form }: ClientFormFieldsProps): JSX.Element {
  const { register, formState: { errors } } = form;

  return (
    <div className="space-y-4">
      {/* Name */}
      <div className="space-y-1">
        <Label htmlFor="client-name">Name *</Label>
        <Input
          id="client-name"
          placeholder="Client name…"
          data-testid="client-name-input"
          {...register('name')}
        />
        {errors.name !== undefined && (
          <p className="text-xs text-destructive" data-testid="client-name-error">
            {errors.name.message}
          </p>
        )}
      </div>

      {/* Code */}
      <div className="space-y-1">
        <Label htmlFor="client-code">Code *</Label>
        <Input
          id="client-code"
          placeholder="ACME (alphanumeric, dashes, underscores)"
          data-testid="client-code-input"
          {...register('code')}
        />
        {errors.code !== undefined && (
          <p className="text-xs text-destructive" data-testid="client-code-error">
            {errors.code.message}
          </p>
        )}
      </div>

      {/* Contact Name */}
      <div className="space-y-1">
        <Label htmlFor="client-contact-name">Contact Name</Label>
        <Input
          id="client-contact-name"
          placeholder="Primary contact…"
          data-testid="client-contact-name-input"
          {...register('contactName')}
        />
        {errors.contactName !== undefined && (
          <p className="text-xs text-destructive">{errors.contactName.message}</p>
        )}
      </div>

      {/* Contact Email — type="text" per Phase 20 ME-02 lesson */}
      <div className="space-y-1">
        <Label htmlFor="client-contact-email">Contact Email</Label>
        <Input
          id="client-contact-email"
          type="text"
          placeholder="contact@example.com"
          data-testid="client-contact-email-input"
          {...register('contactEmail')}
        />
        {errors.contactEmail !== undefined && (
          <p className="text-xs text-destructive" data-testid="client-contact-email-error">
            {errors.contactEmail.message}
          </p>
        )}
      </div>

      {/* Contact Phone */}
      <div className="space-y-1">
        <Label htmlFor="client-contact-phone">Contact Phone</Label>
        <Input
          id="client-contact-phone"
          placeholder="Digits only (e.g. 9876543210)"
          data-testid="client-contact-phone-input"
          {...register('contactPhone')}
        />
        {errors.contactPhone !== undefined && (
          <p className="text-xs text-destructive" data-testid="client-contact-phone-error">
            {errors.contactPhone.message}
          </p>
        )}
      </div>

      {/* Address */}
      <div className="space-y-1">
        <Label htmlFor="client-address">Address</Label>
        <Textarea
          id="client-address"
          rows={2}
          placeholder="Registered address…"
          data-testid="client-address-input"
          {...register('address')}
        />
        {errors.address !== undefined && (
          <p className="text-xs text-destructive">{errors.address.message}</p>
        )}
      </div>

      {/* GST Number */}
      <div className="space-y-1">
        <Label htmlFor="client-gst">GST Number</Label>
        <Input
          id="client-gst"
          placeholder="15ABCDE1234F1Z5"
          data-testid="client-gst-input"
          {...register('gstNumber')}
        />
        {errors.gstNumber !== undefined && (
          <p className="text-xs text-destructive" data-testid="client-gst-error">
            {errors.gstNumber.message}
          </p>
        )}
      </div>

      {/* PAN Number */}
      <div className="space-y-1">
        <Label htmlFor="client-pan">PAN Number</Label>
        <Input
          id="client-pan"
          placeholder="ABCDE1234F"
          data-testid="client-pan-input"
          {...register('panNumber')}
        />
        {errors.panNumber !== undefined && (
          <p className="text-xs text-destructive" data-testid="client-pan-error">
            {errors.panNumber.message}
          </p>
        )}
      </div>

      {/* Notes */}
      <div className="space-y-1">
        <Label htmlFor="client-notes">Notes</Label>
        <Textarea
          id="client-notes"
          rows={3}
          placeholder="Additional notes…"
          data-testid="client-notes-input"
          {...register('notes')}
        />
        {errors.notes !== undefined && (
          <p className="text-xs text-destructive">{errors.notes.message}</p>
        )}
      </div>
    </div>
  );
}
